import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import {
  enablePushNotifications,
  disablePushNotifications,
  getCurrentPushSubscription,
  isPushSupported,
} from '../../lib/push';
import {
  requestAppNotificationPermission,
  showAppNotification,
} from '../../lib/notifications';
import { soundEngine } from '../../lib/sounds';
import { haptics } from '../../lib/haptics';
import {
  Bell,
  Mail,
  Send,
  Webhook,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Play,
  Clock,
  ShieldCheck,
  RefreshCw,
  X,
  ExternalLink,
  ChevronRight,
  Smartphone,
  Download,
} from 'lucide-react';

interface ChannelRecord {
  id: string;
  type: 'EMAIL' | 'TELEGRAM' | 'WEBHOOK';
  name: string;
  enabled: boolean;
  minInactivitySeconds: number;
  config: Record<string, any>;
  totalDispatched: number;
  createdAt: string;
  updatedAt: string;
}

interface LogRecord {
  id: string;
  channelId: string | null;
  channelType: string;
  recipient: string;
  status: string;
  summary: string;
  error: string | null;
  createdAt: string;
  channel?: { name: string; type: string } | null;
}

export function NotificationsPage() {
  const [channels, setChannels] = useState<ChannelRecord[]>([]);
  const [logs, setLogs] = useState<LogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Web Push State
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushActiveDevices, setPushActiveDevices] = useState(0);
  const [pushStatusMessage, setPushStatusMessage] = useState<{ success: boolean; text: string } | null>(null);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    type: 'TELEGRAM' as 'EMAIL' | 'TELEGRAM' | 'WEBHOOK',
    name: 'Manager Telegram Alerts',
    minInactivityMinutes: 2,
    targetEmail: '',
    botToken: '',
    chatId: '',
    webhookUrl: '',
    secretKey: '',
  });

  const checkPushState = async () => {
    setPushSupported(isPushSupported());
    try {
      const res = await api.admin.getPushStatus();
      setPushActiveDevices(res.activeDevices);
      const sub = await getCurrentPushSubscription();
      setPushSubscribed(Boolean(sub));
    } catch {}
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [channelsRes, logsRes] = await Promise.all([
        api.admin.getNotificationChannels(),
        api.admin.getNotificationLogs(),
      ]);
      setChannels(channelsRes.channels);
      setLogs(logsRes.logs);
      await checkPushState();
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notification data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    checkPushState();
  }, []);

  const handleTogglePush = async () => {
    setPushLoading(true);
    setPushStatusMessage(null);
    try {
      if (pushSubscribed) {
        await disablePushNotifications();
        setPushSubscribed(false);
        soundEngine.playPop();
        haptics.impactLight();
        setPushStatusMessage({ success: true, text: 'Push alerts disabled for this device.' });
      } else {
        const res = await requestAppNotificationPermission();
        if (res.success) {
          setPushSubscribed(true);
          soundEngine.playSuccess();
          haptics.notificationSuccess();
          setPushStatusMessage({ success: true, text: 'Push alerts enabled! This device will receive instant popups and harmonic chimes.' });
        } else {
          soundEngine.playError();
          haptics.notificationError();
          setPushStatusMessage({ success: false, text: res.error || 'Permission not granted.' });
        }
      }
      await checkPushState();
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      setPushStatusMessage({ success: false, text: err.message || 'Push toggle failed' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setPushLoading(true);
    setPushStatusMessage(null);
    soundEngine.playTap();
    haptics.impactMedium();
    try {
      const res = await api.admin.testPush();
      setPushStatusMessage({ success: true, text: res.message });
      soundEngine.playChime();
      haptics.notificationSuccess();

      // Immediate test dispatch through unified notification layer (Capacitor/ServiceWorker/Notification)
      await showAppNotification({
        title: '🔔 CK Pulse Test Push Alert',
        body: 'Your device is verified and ready to receive instant workstation inactivity alerts!',
        url: '/admin/notifications',
        tag: 'ckpulse-test',
      });
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      setPushStatusMessage({ success: false, text: err.message || 'Failed to send test push' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleToggleChannel = async (channel: ChannelRecord) => {
    try {
      soundEngine.playTap();
      haptics.selection();
      await api.admin.updateNotificationChannel(channel.id, {
        enabled: !channel.enabled,
      });
      setChannels((prev) =>
        prev.map((c) => (c.id === channel.id ? { ...c, enabled: !c.enabled } : c))
      );
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      alert(err.message || 'Failed to update channel status');
    }
  };

  const handleDeleteChannel = async (id: string) => {
    if (!confirm('Are you sure you want to remove this notification channel?')) return;
    try {
      await api.admin.deleteNotificationChannel(id);
      setChannels((prev) => prev.filter((c) => c.id !== id));
      soundEngine.playPop();
      haptics.impactLight();
    } catch (err: any) {
      alert(err.message || 'Failed to delete channel');
    }
  };

  const handleTestChannel = async (channel: ChannelRecord) => {
    try {
      soundEngine.playTap();
      haptics.impactMedium();
      setTestingId(channel.id);
      setTestResult(null);
      const res = await api.admin.testNotificationChannel(channel.id);
      setTestResult({
        id: channel.id,
        success: res.success,
        message: res.success ? (res.summary || 'Test alert delivered!') : (res.error || 'Failed to deliver test alert'),
      });
      if (res.success) {
        soundEngine.playSuccess();
        haptics.notificationSuccess();
      } else {
        soundEngine.playError();
        haptics.notificationError();
      }
      // Refresh logs
      const logsRes = await api.admin.getNotificationLogs();
      setLogs(logsRes.logs);
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      setTestResult({
        id: channel.id,
        success: false,
        message: err.message || 'Network error during test execution',
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleCreateChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const config: Record<string, any> = {};
      if (formData.type === 'EMAIL') {
        config.targetEmail = formData.targetEmail.trim();
      } else if (formData.type === 'TELEGRAM') {
        config.botToken = formData.botToken.trim();
        config.chatId = formData.chatId.trim();
      } else if (formData.type === 'WEBHOOK') {
        config.webhookUrl = formData.webhookUrl.trim();
        if (formData.secretKey.trim()) {
          config.secretKey = formData.secretKey.trim();
        }
      }

      await api.admin.createNotificationChannel({
        type: formData.type,
        name: formData.name.trim(),
        minInactivitySeconds: formData.minInactivityMinutes * 60,
        config,
      });

      soundEngine.playSuccess();
      haptics.notificationSuccess();
      setModalOpen(false);
      setFormData({
        type: 'EMAIL',
        name: '',
        minInactivityMinutes: 3,
        targetEmail: '',
        botToken: '',
        chatId: '',
        webhookUrl: '',
        secretKey: '',
      });
      fetchData();
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      alert(err.message || 'Failed to create channel');
    } finally {
      setSubmitting(false);
    }
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'EMAIL':
        return <Mail className="w-5 h-5 text-pulse-blue" />;
      case 'TELEGRAM':
        return <Send className="w-5 h-5 text-sky-400" />;
      case 'WEBHOOK':
        return <Webhook className="w-5 h-5 text-violet-400" />;
      default:
        return <Bell className="w-5 h-5 text-zinc-400" />;
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-5 sm:space-y-8 max-w-7xl mx-auto w-full min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white tracking-tight">Notification Center</h1>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-500/10 text-blue-600 dark:text-pulse-blue border border-blue-500/20">
              Multi-Channel Dispatcher
            </span>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Configure automated Email, Telegram Bot, HMAC Webhook, and Browser Push alerts when workstation inactivity exceeds threshold rules
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              soundEngine.playTap();
              haptics.impactLight();
              fetchData();
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-xs font-medium text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 transition active:scale-[0.97]"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Refresh</span>
          </button>

          <button
            onClick={() => {
              soundEngine.playTap();
              haptics.impactLight();
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-pulse-blue hover:bg-blue-600 text-xs font-semibold text-white shadow-md shadow-blue-500/20 transition active:scale-[0.97]"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Channel</span>
          </button>
        </div>
      </div>

      {/* Browser & Mobile Push Notification Banner */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-blue-50/90 via-indigo-50/50 to-white/80 dark:from-blue-950/30 dark:via-zinc-900/60 dark:to-zinc-900/80 border border-blue-200/80 dark:border-blue-500/20 shadow-sm dark:shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-pulse-blue flex items-center justify-center shrink-0 mt-0.5">
              <Smartphone className="w-5 h-5 text-pulse-blue" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Browser & Mobile Web Push (FCM / VAPID)</h2>
                {pushSubscribed ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <CheckCircle2 className="w-3 h-3" />
                    Active on this Device
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">
                    Disabled on this Device
                  </span>
                )}
                {pushActiveDevices > 0 && (
                  <span className="text-[11px] text-zinc-500 dark:text-zinc-400 font-mono">
                    ({pushActiveDevices} admin device{pushActiveDevices > 1 ? 's' : ''} enrolled)
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 max-w-2xl leading-relaxed">
                Receive instant native popups directly on your PC, Mac, Android, or iOS browser when an employee is inactive (e.g. 2-minute alert), even if this tab is in the background or minimized. Zero setup required.
              </p>
              {pushStatusMessage && (
                <div
                  className={`mt-2 text-xs font-medium ${
                    pushStatusMessage.success ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'
                  }`}
                >
                  {pushStatusMessage.text}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 self-start md:self-auto shrink-0 flex-wrap">
            <button
              onClick={handleTogglePush}
              disabled={pushLoading}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition active:scale-[0.97] shadow-sm ${
                pushSubscribed
                  ? 'bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700'
                  : 'bg-pulse-blue hover:bg-blue-600 text-white shadow-blue-500/20'
              }`}
            >
              <Bell className="w-3.5 h-3.5" />
              <span>{pushLoading ? 'Updating...' : pushSubscribed ? 'Disable on this Device' : 'Enable on this Device'}</span>
            </button>

            {pushSubscribed && (
              <button
                onClick={handleTestPush}
                disabled={pushLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white dark:bg-zinc-800/80 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-xs font-medium text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 transition active:scale-[0.97]"
              >
                <Play className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                <span>Test Alert</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Android Native App Card */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-emerald-50/90 via-teal-50/50 to-white/80 dark:from-emerald-950/20 dark:via-zinc-900/60 dark:to-zinc-900/80 border border-emerald-200/80 dark:border-emerald-500/20 shadow-sm dark:shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
              <Smartphone className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">📱 CK Pulse Manager Android App (Capacitor)</h2>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  Ready to Install
                </span>
              </div>
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 max-w-2xl leading-relaxed">
                For managers on mobile devices: install the standalone Android application for 100% reliable push notifications that alert even when the phone screen is locked or the app is closed.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start md:self-auto shrink-0">
            <a
              href="/downloads/ck-pulse-manager.apk"
              download="ck-pulse-manager.apk"
              onClick={() => {
                soundEngine.playPop();
                haptics.impactLight();
              }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition active:scale-[0.97] shadow-sm shadow-emerald-600/20"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download Android App (.apk)</span>
            </a>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs text-rose-500 dark:text-rose-400">
          {error}
        </div>
      )}

      {/* Channels Grid */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-300">Configured Notification Channels</h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-44 rounded-2xl bg-zinc-200 dark:bg-zinc-800/60 animate-pulse" />
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div className="p-8 rounded-2xl bg-white/80 dark:bg-zinc-900/40 border border-zinc-200/80 dark:border-zinc-800/60 text-center space-y-3 shadow-sm dark:shadow-none">
            <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 flex items-center justify-center mx-auto">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">No Alert Channels Configured</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 max-w-md mx-auto mt-1">
                Add an Email recipient, Telegram Bot chat, or Webhook endpoint to receive instant notifications when workstations exceed inactivity thresholds.
              </p>
            </div>
            <button
              onClick={() => {
                soundEngine.playTap();
                haptics.impactLight();
                setModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-xs font-semibold text-zinc-900 dark:text-white border border-zinc-200 dark:border-zinc-700 transition active:scale-[0.97]"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Configure First Channel</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {channels.map((channel) => {
              const isTesting = testingId === channel.id;
              const testInfo = testResult?.id === channel.id ? testResult : null;

              return (
                <div
                  key={channel.id}
                  className={`p-5 rounded-2xl bg-white/80 dark:bg-[#0E0E0E] border flex flex-col justify-between transition shadow-sm ${
                    channel.enabled
                      ? 'border-zinc-200/80 dark:border-zinc-800/90 dark:shadow-lg'
                      : 'border-zinc-200/40 dark:border-zinc-800/40 opacity-70'
                  }`}
                >
                  <div className="space-y-3">
                    {/* Card Top: Icon, Name, Type, and Enabled Toggle */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center">
                          {getChannelIcon(channel.type)}
                        </div>
                        <div>
                          <div className="font-semibold text-xs text-zinc-900 dark:text-white">{channel.name}</div>
                          <span className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 tracking-wider">
                            {channel.type}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleToggleChannel(channel)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase transition active:scale-95 ${
                          channel.enabled
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700'
                        }`}
                      >
                        {channel.enabled ? 'Active' : 'Paused'}
                      </button>
                    </div>

                    {/* Rule summary */}
                    <div className="p-3 rounded-xl bg-zinc-50 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 text-[11px] space-y-1">
                      <div className="text-zinc-600 dark:text-zinc-400 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <span>Threshold Rule:</span>
                        <strong className="text-zinc-900 dark:text-white">
                          ≥ {Math.round(channel.minInactivitySeconds / 60)} minutes idle
                        </strong>
                      </div>
                      <div className="text-zinc-600 dark:text-zinc-400 truncate">
                        <span className="text-zinc-500">Target: </span>
                        <span className="font-mono text-zinc-800 dark:text-zinc-200">
                          {channel.config.targetEmail ||
                            channel.config.chatId ||
                            channel.config.webhookUrl ||
                            'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Test alert feedback toast inline */}
                    {testInfo && (
                      <div
                        className={`p-2.5 rounded-xl text-[11px] flex items-start gap-1.5 ${
                          testInfo.success
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                            : 'bg-rose-500/10 text-rose-500 dark:text-rose-400 border border-rose-500/20'
                        }`}
                      >
                        {testInfo.success ? (
                          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        )}
                        <span className="line-clamp-2">{testInfo.message}</span>
                      </div>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div className="pt-3 mt-3 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500">
                      Dispatched: <strong className="text-zinc-800 dark:text-zinc-300">{channel.totalDispatched}</strong>
                    </span>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleTestChannel(channel)}
                        disabled={isTesting}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700/80 transition active:scale-95 disabled:opacity-50"
                      >
                        <Play className={`w-3 h-3 ${isTesting ? 'animate-spin' : ''}`} />
                        <span>{isTesting ? 'Testing...' : 'Send Test'}</span>
                      </button>

                      <button
                        onClick={() => handleDeleteChannel(channel.id)}
                        className="p-1 rounded-lg text-zinc-400 hover:text-rose-500 hover:bg-rose-500/10 transition active:scale-95"
                        title="Delete Channel"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delivery Logs Audit Trail */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-300">Recent Dispatch & Delivery Logs</h2>

        <div className="rounded-2xl border border-zinc-200/80 dark:border-[#222222] bg-white/90 dark:bg-[#0E0E0E] overflow-hidden shadow-sm dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
              <thead className="bg-zinc-100/80 dark:bg-zinc-900/90 text-zinc-600 dark:text-zinc-400 border-b border-zinc-200/80 dark:border-zinc-800 font-medium">
                <tr>
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Channel Type</th>
                  <th className="px-4 py-3">Recipient / Target</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Delivery Summary</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200/60 dark:divide-zinc-800/60">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                      Loading dispatch logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                      No notifications have been dispatched yet.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const isDelivered = log.status === 'DELIVERED';

                    return (
                      <tr key={log.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition">
                        <td className="px-4 py-3 font-mono text-[11px] text-zinc-500 dark:text-zinc-400">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>

                        <td className="px-4 py-3 font-medium">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                            {log.channelType}
                          </span>
                        </td>

                        <td className="px-4 py-3 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                          {log.recipient}
                        </td>

                        <td className="px-4 py-3">
                          {isDelivered ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Delivered</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 dark:text-rose-400 border border-rose-500/20">
                              <AlertTriangle className="w-3 h-3" />
                              <span>Failed</span>
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                          <div>{log.summary}</div>
                          {log.error && (
                            <div className="text-[10px] text-rose-500 dark:text-rose-400 font-mono mt-0.5">
                              Error: {log.error}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Channel Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-[#121212] border border-zinc-200 dark:border-zinc-800 p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-pulse-blue" />
                <h3 className="font-semibold text-zinc-900 dark:text-white text-base">Configure Notification Channel</h3>
              </div>
              <button
                onClick={() => {
                  soundEngine.playPop();
                  setModalOpen(false);
                }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-white transition p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateChannel} className="space-y-4">
              {/* Type Switcher */}
              <div>
                <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1.5">Channel Provider</label>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      { type: 'EMAIL', label: 'Email', icon: <Mail className="w-4 h-4" /> },
                      { type: 'TELEGRAM', label: 'Telegram', icon: <Send className="w-4 h-4" /> },
                      { type: 'WEBHOOK', label: 'Webhook', icon: <Webhook className="w-4 h-4" /> },
                    ] as const
                  ).map((t) => (
                    <button
                      key={t.type}
                      type="button"
                      onClick={() => {
                        soundEngine.playTap();
                        haptics.selection();
                        setFormData({ ...formData, type: t.type });
                      }}
                      className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-semibold border transition active:scale-95 ${
                        formData.type === t.type
                          ? 'bg-pulse-blue/15 text-pulse-blue border-pulse-blue/40 shadow-sm'
                          : 'bg-zinc-50 dark:bg-zinc-900/60 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-200'
                      }`}
                    >
                      {t.icon}
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">Channel Label</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. IT Operations Team Email"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                />
              </div>

              {/* Threshold Rule */}
              <div>
                <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">
                  Inactivity Trigger Threshold
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { label: '1 min (Instant)', min: 1 },
                    { label: '2 mins (Strict Alert)', min: 2 },
                    { label: '3 mins', min: 3 },
                    { label: '5 mins', min: 5 },
                    { label: '15 mins (Escalation)', min: 15 },
                  ].map((p) => (
                    <button
                      key={p.min}
                      type="button"
                      onClick={() => {
                        soundEngine.playTap();
                        haptics.selection();
                        setFormData({ ...formData, minInactivityMinutes: p.min });
                      }}
                      className={`px-2.5 py-1 text-xs rounded-xl font-mono transition-all active:scale-95 ${
                        formData.minInactivityMinutes === p.min
                          ? 'bg-pulse-blue text-white dark:text-black font-bold shadow-sm'
                          : 'bg-zinc-100 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    required
                    value={formData.minInactivityMinutes}
                    onChange={(e) =>
                      setFormData({ ...formData, minInactivityMinutes: parseInt(e.target.value) || 1 })
                    }
                    className="w-24 px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white text-center font-bold focus:outline-none focus:border-pulse-blue"
                  />
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    Dispatches alert once continuous inactivity reaches{' '}
                    <strong className="text-amber-500 dark:text-amber-400">{formData.minInactivityMinutes} minutes</strong> ({formData.minInactivityMinutes * 60}s).
                  </span>
                </div>
              </div>

              {/* Dynamic Type Fields */}
              {formData.type === 'EMAIL' && (
                <div>
                  <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">Recipient Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="it-ops@yourcompany.com"
                    value={formData.targetEmail}
                    onChange={(e) => setFormData({ ...formData, targetEmail: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                  />
                </div>
              )}

              {formData.type === 'TELEGRAM' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">Telegram Bot Token</label>
                    <input
                      type="text"
                      required
                      placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                      value={formData.botToken}
                      onChange={(e) => setFormData({ ...formData, botToken: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white font-mono placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">Telegram Chat ID</label>
                    <input
                      type="text"
                      required
                      placeholder="-100123456789 or @channel"
                      value={formData.chatId}
                      onChange={(e) => setFormData({ ...formData, chatId: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white font-mono placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                    />
                  </div>
                </div>
              )}

              {formData.type === 'WEBHOOK' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">Webhook Endpoint URL</label>
                    <input
                      type="url"
                      required
                      placeholder="https://api.yourcompany.com/webhooks/ck-pulse"
                      value={formData.webhookUrl}
                      onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium block mb-1">
                      Secret Key for HMAC-SHA256 (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="whsec_secret_key_..."
                      value={formData.secretKey}
                      onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-zinc-900 dark:text-white font-mono placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-pulse-blue"
                    />
                    <span className="text-[11px] text-zinc-500 block mt-1">
                      Payloads are signed in the <code className="text-zinc-600 dark:text-zinc-400">X-Pulse-Signature</code> header.
                    </span>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playPop();
                    setModalOpen(false);
                  }}
                  className="px-4 py-2 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold bg-pulse-blue hover:bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20 transition active:scale-[0.97] disabled:opacity-50"
                >
                  {submitting ? 'Saving Channel...' : 'Save & Enable Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
